import type {
  ExternalUrl,
  Folder,
  PlayData,
  PlaySettings,
  Playbook,
  PlaybookAsset,
} from '@carboncopyco/types';
import { removeUndefinedValues } from '@carboncopyco/utils';
import type { EndpointDefinitions } from '@reduxjs/toolkit/query';
import type { QueryKeys } from '@reduxjs/toolkit/src/query/core/apiState';
import { folderApi, workspaceApi } from '../mainApi';
import {
  createOnQueryStartedPerContext,
  queryDataUpdateHandler,
  workspaceAddAsset,
  workspaceRemoveAsset,
} from '../utils/queryDataUpdateHandler';
import { mainApi } from './mainApi';

export type BasePlaybookArgs = {
  organizationId: string;
  id: string;
  workspaceId?: string;
  folderId?: Folder['id'];
};

export type GetPlaybookArgs = BasePlaybookArgs & {
  removeDeleted?: boolean;
};

export type CreatePlaybookArgs = {
  organizationId: string;
  workspaceId: string;
  folderId?: string;
  name: string;
  description?: string;
  assets: Omit<PlaybookAsset, 'entity'>[];
  playSettings?: PlaySettings;
};

export type UpdatePlaybookArgs = BasePlaybookArgs & {
  name?: string;
  description?: string;
  assets?: PlaybookAsset[];
  withOptimisticUpdate?: boolean;
  playSettings?: PlaySettings;
};

export type CopyMovePlaybookArgs = BasePlaybookArgs & {
  folderId: string;
  currentFolderId?: string;
};

export type CreateExternalUrlArgs = {
  organizationId: string;
  workspaceId?: string;
  folderId?: string;
  name: string;
  url: string;
};
export type GetExternalUrlArgs = {
  organizationId: string;
  id: string;
};

export type UpdateExternalUrlArgs = {
  organizationId: string;
  id: string;
  name: string;
};
export type DeleteExternalUrlArgs = {
  organizationId: string;
  id: string;
};

export type RestoreExternalUrlArgs = {
  organizationId: string;
  id: string;
};

const playbookApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    getPlaybook: builder.query<Playbook, GetPlaybookArgs>({
      query: ({ organizationId, workspaceId, id, removeDeleted = false }) => ({
        url: `/api/${organizationId}/playbook/${id}`,
        params: {
          workspaceId,
          removeDeleted: String(removeDeleted),
        },
      }),
      providesTags: (_, __, { id }) => [{ type: 'Playbook', id }],
      transformResponse: (playbook: Playbook) => {
        return {
          ...playbook,
          assets: playbook.assets.map((asset) => ({
            ...asset,
            assetId: String(asset.assetId),
          })),
        };
      },
    }),
    createPlaybook: builder.mutation<Playbook, CreatePlaybookArgs>({
      query: ({ organizationId, ...rest }) => ({
        method: 'POST',
        url: `/api/${organizationId}/playbook`,
        body: removeUndefinedValues(rest),
      }),
      async onQueryStarted(_, { dispatch, getState, queryFulfilled }) {
        const state = getState();
        const { data: createdPlaybook } = await queryFulfilled;
        await workspaceAddAsset({
          queryFulfilled,
          state,
          dispatch,
          argumentKey:
            String(createdPlaybook.folderId) === '0'
              ? 'workspaceId'
              : 'folderId',
          entityType: 'playbook',
        });
      },
      invalidatesTags: (res, __, args) => [
        { type: 'WorkspaceAssets', id: args.workspaceId },
        { type: 'FolderAssets', id: args.folderId },
        { type: 'Playbook', id: res.id },
      ],
    }),
    updatePlaybook: builder.mutation<Playbook, UpdatePlaybookArgs>({
      query: ({
        organizationId,
        id,
        description,
        assets,
        name,
        playSettings,
      }) => ({
        url: `/api/${organizationId}/playbook/${id}`,
        method: 'POST',
        body: removeUndefinedValues({
          name,
          description,
          assets,
          playSettings,
        }),
      }),
      onQueryStarted: async (
        {
          organizationId,
          workspaceId,
          id,
          name,
          description,
          withOptimisticUpdate,
        },
        { dispatch, queryFulfilled }
      ) => {
        if (withOptimisticUpdate) {
          const patchResult = dispatch(
            playbookApi.util.updateQueryData(
              'getPlaybook',
              { organizationId, workspaceId, id },
              (playbook) => {
                if (playbook) {
                  playbook.name = name;
                  playbook.description = description;
                }
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
          return;
        }
        return;
      },
      invalidatesTags: (res) => [
        { type: 'Playbook', id: res.id },
        { type: 'WorkspaceAssets', id: res.workspaceId },
        { type: 'FolderAssets', id: res.folderId },
      ],
    }),
    deletePlaybook: builder.mutation<Playbook, BasePlaybookArgs>({
      query: ({ organizationId, id }) => ({
        url: `/api/${organizationId}/playbook/${id}`,
        method: 'DELETE',
      }),
      onQueryStarted: createOnQueryStartedPerContext(),
      invalidatesTags: (res, __, { folderId, id }) =>
        res
          ? [
              {
                type: 'WorkspaceAssets',
                id: res.workspaceId,
              },
              { type: 'FolderAssets', id: folderId },
            ]
          : [{ type: 'Playbook', id }],
    }),
    duplicatePlaybook: builder.mutation<Playbook, BasePlaybookArgs>({
      query: ({ organizationId, id }) => ({
        url: `/api/${organizationId}/playbook/${id}/duplicate`,
        method: 'POST',
      }),
      async onQueryStarted(_, { dispatch, getState, queryFulfilled }) {
        const state = getState();
        const { data: duplicatedPlaybook } = await queryFulfilled;
        await workspaceAddAsset({
          queryFulfilled,
          state,
          dispatch,
          argumentKey:
            String(duplicatedPlaybook.folderId) === '0'
              ? 'workspaceId'
              : 'folderId',
          entityType: 'playbook',
        });
      },
      invalidatesTags: (res) => [
        { type: 'Playbook' },
        { type: 'WorkspaceAssets', id: res.workspaceId },
        { type: 'FolderAssets', id: res.folderId },
      ],
    }),
    copyPlaybook: builder.mutation<Playbook, CopyMovePlaybookArgs>({
      query: ({ organizationId, id, folderId }) => ({
        url: `/api/${organizationId}/playbook/${id}/copy`,
        method: 'POST',
        body: { folderId },
      }),
      invalidatesTags: (res) => [
        { type: 'Playbook' },
        { type: 'WorkspaceAssets', id: res.workspaceId },
        { type: 'FolderAssets', id: res.folderId },
        { type: 'Folders', id: res.workspaceId },
      ],
    }),
    movePlaybook: builder.mutation<Playbook, CopyMovePlaybookArgs>({
      query: ({ organizationId, id, folderId }) => ({
        url: `/api/${organizationId}/playbook/${id}/move`,
        method: 'POST',
        body: { folderId },
      }),
      onQueryStarted: async (arg, { dispatch, getState, queryFulfilled }) => {
        try {
          const { data: movePlaybookResponse } = await queryFulfilled;
          const isMovingFromWorkspaceToFolder =
            arg.folderId !== '0' && arg.currentFolderId === '0';
          const state = getState();
          queryDataUpdateHandler({
            api: isMovingFromWorkspaceToFolder ? workspaceApi : folderApi,
            state,
            dispatch,
            endpointName: isMovingFromWorkspaceToFolder
              ? ('getWorkspaceAssets' as QueryKeys<EndpointDefinitions>)
              : ('getFolderAssets' as QueryKeys<EndpointDefinitions>),
            endpointArgumentKey: isMovingFromWorkspaceToFolder
              ? 'workspaceId'
              : 'folderId',
            endpointArgumentValue: isMovingFromWorkspaceToFolder
              ? movePlaybookResponse.workspaceId
              : arg.currentFolderId,
            updateQueryDataFunction: (assets) =>
              workspaceRemoveAsset(assets, arg),
          });
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: (res) => [
        { type: 'Playbook' },
        { type: 'WorkspaceAssets', id: res.workspaceId },
        { type: 'FolderAssets', id: res.folderId },
        { type: 'Folders', id: res.workspaceId },
      ],
    }),
    getPlaybookPreview: builder.query<PlayData, BasePlaybookArgs>({
      query: ({ organizationId, id }) => ({
        url: `/api/${organizationId}/playbook/${id}/preview`,
      }),
      providesTags: (_, __, { id }) => [{ type: 'Playbook', id }],
    }),
    restorePlaybook: builder.mutation<Playbook, BasePlaybookArgs>({
      query: ({ organizationId, id }) => ({
        url: `/api/${organizationId}/playbook/${id}/restore`,
        method: 'POST',
      }),
      onQueryStarted: async (arg, { dispatch, getState, queryFulfilled }) => {
        try {
          const { data: restorePlaybook } = await queryFulfilled;
          const state = getState();
          queryDataUpdateHandler({
            state,
            dispatch,
            endpointName:
              'getWorkspaceAssets' as QueryKeys<EndpointDefinitions>,
            endpointArgumentKey: 'workspaceId',
            endpointArgumentValue: restorePlaybook.workspaceId,
            updateQueryDataFunction: (workspaceAssets) =>
              workspaceRemoveAsset(workspaceAssets, arg),
          });
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: (res) => [
        { type: 'Playbook' },
        { type: 'WorkspaceAssets', id: res.workspaceId },
        { type: 'FolderAssets', id: res.folderId },
      ],
    }),
    createExternalUrl: builder.mutation<ExternalUrl, CreateExternalUrlArgs>({
      query: ({ organizationId, ...rest }) => ({
        method: 'POST',
        url: `/api/${organizationId}/external-url`,
        body: removeUndefinedValues(rest),
      }),
    }),
    getExternalUrl: builder.query<ExternalUrl, GetExternalUrlArgs>({
      query: ({ organizationId, id }) => ({
        method: 'GET',
        url: `/api/${organizationId}/external-url/${id}`,
      }),
    }),
    updateExternalUrl: builder.mutation<ExternalUrl, UpdateExternalUrlArgs>({
      query: ({ organizationId, id, ...rest }) => ({
        method: 'POST',
        url: `/api/${organizationId}/external-url/${id}`,
        body: removeUndefinedValues(rest),
      }),
    }),
    deleteExternalUrl: builder.mutation<ExternalUrl, DeleteExternalUrlArgs>({
      query: ({ organizationId, id }) => ({
        method: 'DELETE',
        url: `/api/${organizationId}/external-url/${id}`,
      }),
    }),
    restoreExternalUrl: builder.mutation<ExternalUrl, RestoreExternalUrlArgs>({
      query: ({ organizationId, id }) => ({
        method: 'POST',
        url: `/api/${organizationId}/external-url/${id}/restore`,
      }),
    }),
  }),
});

export const {
  useGetPlaybookQuery,
  useLazyGetPlaybookPreviewQuery,
  useLazyGetPlaybookQuery,
  useCreatePlaybookMutation,
  useUpdatePlaybookMutation,
  useDeletePlaybookMutation,
  useDuplicatePlaybookMutation,
  useCopyPlaybookMutation,
  useMovePlaybookMutation,
  useRestorePlaybookMutation,
  useCreateExternalUrlMutation,
  useGetExternalUrlQuery,
  useUpdateExternalUrlMutation,
  useDeleteExternalUrlMutation,
  useRestoreExternalUrlMutation,
} = playbookApi;
